import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, CheckCircle2, Clock, Loader2, Play, Plus, Soup, Square, Trash2, Users, Tag, Pencil,
} from 'lucide-react';
import { confirm, alertDialog } from '../../components/ui/confirm-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import BuffetCounter from './BuffetCounter';
import CapacityPanel from './CapacityPanel';
import BuffetMonthCalendar from './BuffetMonthCalendar';
import OpeningWizard from './OpeningWizard';
import { useRestaurant } from '../../context/RestaurantContext';
import { todayISO } from '../../lib/utils';
import apiClient from '../../services/api';
import { longDate, rupees, shortDate, t12 } from './openingHelpers';

/** Open Buffets: which days a template runs, with each day owning the values copied onto it. */

/* What the card says about the service's life. A service is STARTED by a
   person and ENDS by the clock (or early, by a person) — so a day that was
   never started says so plainly instead of sitting there with no state:
   "not started yet" while its window is still open, "never started" once
   the window has passed and starting is no longer possible. */
const lifeChip = (o, isToday) => {
  if (o.status === 'RUNNING') {
    return ['LIVE', 'border-orange-300 bg-orange-50 text-orange-700',
      'Being served right now — it ends by itself at closing time, or from the End button before that.'];
  }
  if (o.status === 'CLOSED') {
    return ['Ended', 'border-slate-200 bg-slate-100 text-slate-600',
      'This service has ended — at its closing time, or by somebody ending it early.'];
  }
  if (o.window_over) {
    return ['Never started', 'border-red-200 bg-red-50 text-red-600',
      'The service window passed with nobody starting this buffet — it never ran, and that is its record.'];
  }
  if (isToday) {
    return ['Not started yet', 'border-amber-300 bg-amber-50 text-amber-700',
      'Waiting to be started — a buffet opens when somebody presses Start, not by the clock.'];
  }
  return [];
};

export default function OpenBuffets() {
  const { restaurantId } = useRestaurant();
  const [date, setDate] = useState(todayISO);
  const [month, setMonth] = useState(() => todayISO().slice(0, 7));
  const [monthDays, setMonthDays] = useState({});
  const [day, setDay] = useState(null);          // {openings, templates}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // What the wizard is doing: null | {} (opening a new one) | {opening}
  const [editing, setEditing] = useState(null);
  /* The dates a pending repeat would open on, lifted out of the wizard so the calendar can outline them. */
  const [preview, setPreview] = useState([]);
  const [expanded, setExpanded] = useState(false);
  /* The service whose counter is open. A started buffet is answered from here
     rather than from another screen: starting one and then saying what has
     gone out to the counter is a single job, and it is done standing in the
     hall with a pan in the other hand. */
  const [servingFor, setServingFor] = useState(null);
  /* Capacity opens in its own window: two charts and a bookings table would push every other buffet off the day. */
  const [capacityFor, setCapacityFor] = useState(null);

  const loadMonth = useCallback(() => {
    if (!restaurantId) return;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/openings/`, { params: { month } })
      .then(({ data }) => setMonthDays(data?.success ? (data.data.days || {}) : {}))
      .catch(() => setMonthDays({}));
  }, [restaurantId, month]);

  const loadDay = useCallback((silent = false) => {
    if (!restaurantId) return;
    if (!silent) setLoading(true);
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/openings/`, { params: { date } })
      .then(({ data }) => {
        if (data?.success) { setDay(data.data); setError(''); } else {
          setError(data?.error || 'Could not read that day.');
        }
      })
      .catch((e) => setError(e?.response?.data?.error || e.message || 'Could not read that day.'))
      .finally(() => setLoading(false));
  }, [restaurantId, date]);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadDay(); }, [loadDay]);
  /* A service ends BY CLOCK, and the clock can reach closing time while
     somebody is looking at the day — so today is re-read every minute,
     silently, and a LIVE card turns to Ended without anyone refreshing. */
  useEffect(() => {
    if (date !== todayISO()) return undefined;
    const tick = setInterval(() => loadDay(true), 60000);
    return () => clearInterval(tick);
  }, [date, loadDay]);
  // Changing the day closes whatever was being edited, since the form belongs to a date.
  useEffect(() => {
    setEditing(null); setCapacityFor(null); setPreview([]); setNotice('');
  }, [date]);

  const isPast = date < todayISO();
  const dayLabel = useMemo(() => longDate(date), [date]);
  const highlight = useMemo(() => new Set(preview), [preview]);
  const openings = day?.openings || [];

  const save = async (body) => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      if (editing?.opening) {
        const { data } = await apiClient.put(
          `/users/restaurant/${restaurantId}/buffets/openings/${editing.opening.id}/`, body);
        if (!data?.success) throw new Error(data?.error || 'Could not save');
        setNotice(`${data.data?.name || 'The buffet'} saved for ${dayLabel}.`);
      } else {
        const { data } = await apiClient.post(
          `/users/restaurant/${restaurantId}/buffets/openings/`, body);
        if (!data?.success) throw new Error(data?.error || 'Could not create that buffet');
        /* A repeat can meet dates already open; those are left as they are and said so, rather than silently skipped. */
        const made = data.created || [];
        const skipped = data.skipped || [];
        const name = data.data?.name || 'The buffet';
        setNotice(made.length > 1
          ? `${name} created on ${made.length} dates — ${shortDate(made[0])} to ${shortDate(made[made.length - 1])}.`
            + (skipped.length ? ` ${skipped.length} already open and left as ${skipped.length === 1 ? 'it was' : 'they were'}.` : '')
          : `${name} created for ${dayLabel}.`);
        /* A repeat now opens what it can rather than throwing the whole run
           away over one bad day, so the dates it could NOT open have to be
           said out loud — otherwise the notice above quietly overstates what
           happened. The server's own sentence, the way every other refusal on
           this screen is reported. */
        const notMade = data.failed || [];
        if (data.stopped || notMade.length) {
          setError(data.stopped
            || `${notMade.length} date${notMade.length === 1 ? '' : 's'} could not be opened. ${notMade[0].error}`);
        }
      }
      setEditing(null);
      setPreview([]);
      loadDay();
      loadMonth();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not save');
      /* The day is re-read even when the write was refused, since the panel settles which of them is true. */
      loadDay();
      loadMonth();
    } finally {
      setSaving(false);
    }
  };

  /* Open the counter for real. Before the hour the day advertises, that is a
     decision somebody may be asked about later — so it is confirmed in those
     words and the server writes STARTED_EARLY with the minutes into the
     service's audit trail. */
  const [starting, setStarting] = useState(null);
  const start = async (o) => {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const early = date === todayISO() && o.start_time && hhmm < String(o.start_time).slice(0, 5);
    const ok = await confirm(early
      ? {
          title: `Start ${o.name} early?`,
          description: `It is not due to open until ${t12(o.start_time)}. Starting now opens the `
            + 'counter and lets the till sell covers. It is logged as an early start, with who '
            + 'started it and how far ahead.',
          confirmText: 'Start it now',
          cancelText: 'Wait',
        }
      : {
          title: `Start ${o.name}?`,
          description: 'The counter opens and the till can sell covers.',
          confirmText: 'Start service',
        });
    if (!ok) return;
    setStarting(o.id);
    setError('');
    try {
      const { data } = await apiClient.put(
        `/users/restaurant/${restaurantId}/buffets/${o.buffet_id}/service-plan/`,
        { date, status: 'RUNNING' });
      if (!data?.success) throw new Error(data?.error || 'Could not start the service');
      setNotice(`${o.name} is being served${early ? ' — started early, and logged as such.' : '.'}`);
      loadDay();
      loadMonth();
      /* Straight into the counter: the next thing anybody does after starting
         a buffet is say what has gone out on it, and making them find another
         screen for that is how a first batch goes unrecorded. */
      setServingFor(o);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not start the service');
    } finally {
      setStarting(null);
    }
  };

  /* End the service by hand, before the clock does. Ending is normally not a
     decision at all — the service closes itself at the day's end time — so a
     press of this button IS one, and before the advertised hour it is
     confirmed in those words and lands in the diary as ENDED_EARLY with who. */
  const [ending, setEnding] = useState(null);
  const endService = async (o) => {
    const now = new Date();
    const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const early = date === todayISO() && o.end_time && hhmm < String(o.end_time).slice(0, 5);
    const ok = await confirm(early
      ? {
          title: `End ${o.name} early?`,
          description: `It is meant to run until ${t12(o.end_time)} — left alone, it ends itself then. `
            + 'Ending it now closes the counter and stops the till selling covers, and it is '
            + 'logged as an early end, with who ended it and how far ahead of the hour.',
          confirmText: 'End it now',
          cancelText: 'Keep serving',
          tone: 'destructive',
        }
      : {
          title: `End ${o.name}?`,
          description: 'The counter closes and the till stops selling covers.',
          confirmText: 'End service',
        });
    if (!ok) return;
    setEnding(o.id);
    setError('');
    try {
      const { data } = await apiClient.put(
        `/users/restaurant/${restaurantId}/buffets/${o.buffet_id}/service-plan/`,
        { date, status: 'CLOSED' });
      if (!data?.success) throw new Error(data?.error || 'Could not end the service');
      setNotice(`${o.name} has ended${early ? ' — before its hour, and logged as such.' : '.'}`);
      loadDay();
      loadMonth();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not end the service');
    } finally {
      setEnding(null);
    }
  };

  const remove = async (o) => {
    const ok = await confirm({
      title: `Take ${o.name} off ${dayLabel}?`,
      description: 'The buffet template is untouched — only this day\'s service is removed.',
      confirmText: 'Take it off',
      cancelText: 'Keep it',
      tone: 'destructive',
    });
    if (!ok) return;
    try {
      const { data } = await apiClient.delete(
        `/users/restaurant/${restaurantId}/buffets/openings/${o.id}/`);
      if (!data?.success) throw new Error(data?.error || 'Could not remove it');
      loadDay();
      loadMonth();
    } catch (e) {
      alertDialog({
        title: 'Could not take it off',
        description: e?.response?.data?.error || e.message,
      });
    }
  };

  const calendar = (
    <BuffetMonthCalendar
      month={month} onMonth={setMonth}
      selected={date} onSelect={setDate} days={monthDays}
      expanded={expanded} onToggleExpanded={() => setExpanded((v) => !v)}
      highlight={highlight}
    />
  );

  const wizard = editing && (
    <OpeningWizard
      key={editing.opening ? `o${editing.opening.id}` : `n${date}`}
      restaurantId={restaurantId}
      date={date}
      templates={day?.templates || []}
      opening={editing.opening || null}
      saving={saving}
      onCancel={() => { setEditing(null); setPreview([]); }}
      onSave={save}
      onRepeatPreview={setPreview}
    />
  );

  const dayList = (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">{dayLabel}</h2>
          <p className="text-xs text-muted-foreground">
            {loading ? 'Reading the day…'
              : `${openings.length} buffet${openings.length === 1 ? '' : 's'} open`}
          </p>
        </div>
        {/* Opening a past day is refused by the server too, so the button is simply not offered. */}
        {!isPast && !editing && (
          <button type="button" onClick={() => { setEditing({}); setNotice(''); }}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            <Plus size={15} /> Create a buffet
          </button>
        )}
      </div>

      {loading ? (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 size={15} className="animate-spin" /> Reading the day…
        </p>
      ) : openings.length === 0 && !editing ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <CalendarDays className="mx-auto mb-3 h-10 w-10 opacity-30" />
          {isPast
            ? 'No buffet ran on this day.'
            : <>No buffet is open on this day yet.{' '}
              <button type="button" onClick={() => setEditing({})}
                className="font-medium text-primary hover:underline">Create one</button>.</>}
        </div>
      ) : (
        <div className="space-y-2">
          {openings.map((o) => {
            const [chip, cls, chipTitle] = lifeChip(o, date === todayISO());
            const dishes = (o.items || []).length;
            return (
              <div key={o.id} className="rounded-xl border border-border bg-card p-4">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="font-semibold text-foreground">{o.name}</span>
                  {chip && (
                    <span title={chipTitle}
                      className={`rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase ${cls}`}>
                      {chip}
                    </span>
                  )}
                  {o.name !== o.template_name && (
                    <span className="text-xs text-muted-foreground">
                      from <strong className="text-foreground">{o.template_name}</strong>
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock size={12} /> {t12(o.start_time) || '…'} – {t12(o.end_time) || '…'}
                  </span>
                  {/* The FACTS beside the schedule: when somebody actually pressed
                      Start, and when the service actually ended — by hand or by
                      the clock — read from the day's diary. */}
                  {o.started_at && (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700"
                      title={o.started_by
                        ? `Started by ${o.started_by}`
                        : 'When the service was started'}>
                      <Play size={11} /> started {t12(o.started_at)}
                    </span>
                  )}
                  {o.ended_at && (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-600"
                      title={o.ended_auto
                        ? 'Ended by the clock at closing time'
                        : (o.ended_by ? `Ended by ${o.ended_by}` : 'When the service ended')}>
                      <Square size={10} /> ended {t12(o.ended_at)}{o.ended_auto ? ' (by the clock)' : ''}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Tag size={12} /> {o.adult_price != null ? rupees(o.adult_price) : '—'} / adult
                    {o.child_price != null && ` · ${rupees(o.child_price)} / kid`}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Users size={12} /> {o.covers_planned ?? '—'} covers
                    {o.covers_sold > 0 && ` · ${o.covers_sold} sold`}
                  </span>
                  {/* What is actually going out: the day's counter is set on this screen, so the card says whether it has one. */}
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Soup size={12} />
                    {dishes ? `${dishes} dish${dishes === 1 ? '' : 'es'}` : 'no dishes yet'}
                  </span>
                  <span className="ml-auto flex items-center gap-2">
                    {/* Capacity belongs HERE, on a date, in its own window rather than shoving the day off screen. */}
                    {/* Only on the day, and only once: a service cannot be
                        started before its date or started twice. */}
                    {/* A service is started on the day it runs, so on any other
                        day the card says so rather than leaving somebody
                        hunting for a button that is deliberately absent. */}
                    {o.status === 'PLANNED' && date > todayISO() && (
                      <span className="text-xs text-muted-foreground"
                        title="A buffet is started on the day it runs — come back on the day, or start it from here then.">
                        Starts on the day
                      </span>
                    )}
                    {/* Only while the window is still open: once it has passed
                        a never-started day stays that way — the chip says so —
                        and the server would refuse the start anyway. */}
                    {o.status === 'PLANNED' && date === todayISO() && !o.window_over && (
                      <button type="button"
                        disabled={starting === o.id}
                        onClick={() => start(o)}
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                        <Play size={12} /> {starting === o.id ? 'Starting…' : 'Start'}
                      </button>
                    )}
                    {/* Ending needs no button press — the service closes itself
                        at the day's end time — so this one is only for calling
                        it off early, and says so before it does. */}
                    {o.status === 'RUNNING' && (
                      <button type="button"
                        disabled={ending === o.id}
                        onClick={() => endService(o)}
                        title={`Ends by itself at ${t12(o.end_time) || 'closing time'} — this ends it now`}
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2.5 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-60">
                        <Square size={11} /> {ending === o.id ? 'Ending…' : 'End now'}
                      </button>
                    )}
                    {/* Once it is running the counter is the screen that matters:
                        what is out, what has gone, and the batches going out
                        behind it. Offered on a closed day too, where it reads
                        as the record of what was actually cooked. */}
                    {(o.status === 'RUNNING' || o.status === 'CLOSED') && (
                      <button type="button"
                        onClick={() => setServingFor(o)}
                        aria-haspopup="dialog"
                        title={o.status === 'RUNNING'
                          ? `What is on ${o.name}'s counter — and add the batches going out`
                          : `What ${o.name} cooked and served that day`}
                        className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold ${
                          o.status === 'RUNNING'
                            ? 'bg-orange-600 text-white hover:bg-orange-700'
                            : 'border border-border font-medium text-slate-700 hover:bg-slate-50'}`}>
                        <Soup size={12} /> {o.status === 'RUNNING' ? 'Counter & batches' : 'Counter'}
                      </button>
                    )}
                    <button type="button"
                      onClick={() => setCapacityFor(o)}
                      aria-haspopup="dialog"
                      className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                      <Users size={12} /> {isPast ? 'Who came' : 'View capacity'}
                    </button>
                    {/* A day that has been is a record: the server refuses to
                        change or remove it, so neither button is offered. The
                        read views — the counter, who came — stay. */}
                    {!isPast && (
                      <button type="button"
                        onClick={() => { setEditing({ opening: o }); setNotice(''); }}
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50">
                        <Pencil size={12} /> Edit this day
                      </button>
                    )}
                    {/* A service that is running, HAS RUN, or has sold covers is
                        refused by the server — what happened is a record — so
                        the button is only offered while nothing has. */}
                    {!isPast && o.status !== 'RUNNING' && o.status !== 'CLOSED' && o.covers_sold === 0 && (
                      <button type="button" onClick={() => remove(o)}
                        aria-label={`Take ${o.name} off this date`}
                        className="rounded-md border border-red-200 px-2 py-1.5 text-red-600 hover:bg-red-50">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </span>
                </div>
                {o.notes && (
                  <p className="mt-2 text-xs italic text-muted-foreground">{o.notes}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 w-full">
      <header className="mb-5">
        <h1 className="font-display text-3xl font-semibold leading-none tracking-tight text-foreground">
          Open Buffets
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Put a template onto a date — one day, or a whole run.
        </p>
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-primary/30 bg-primary/[0.06] px-4 py-2.5 text-sm text-foreground">
          <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-primary" /> {notice}
        </div>
      )}

      {/* Expanded, the calendar takes the room and the form drops underneath, keeping its full width. */}
      {expanded ? (
        <>
          <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_400px]">
            {calendar}
            {dayList}
          </div>
          {wizard && <div className="mt-5">{wizard}</div>}
        </>
      ) : (
        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
          {calendar}
          <div>
            {wizard && <div className="mb-4">{wizard}</div>}
            {dayList}
          </div>
        </div>
      )}

      {/* Mounted only while open, so the counter's own polling stops with it. */}
      <Dialog open={!!servingFor}
        onOpenChange={(open) => { if (!open) { setServingFor(null); loadDay(); } }}>
        <DialogContent className="sm:max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Soup size={16} />
              {servingFor?.name}
              <span className="font-normal text-muted-foreground">· {dayLabel}</span>
            </DialogTitle>
          </DialogHeader>
          {servingFor && (
            <BuffetCounter
              embedded
              restaurantId={restaurantId}
              buffet={{ id: servingFor.buffet_id, name: servingFor.name }}
              date={date}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Mounted only while open, so its polling stops on close and never starts for a day that is over. */}
      <Dialog open={!!capacityFor} onOpenChange={(o) => !o && setCapacityFor(null)}>
        <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users size={16} />
              {isPast ? 'Who came' : 'Capacity'} — {capacityFor?.name}
              <span className="font-normal text-muted-foreground">· {dayLabel}</span>
            </DialogTitle>
          </DialogHeader>
          {capacityFor && (
            <CapacityPanel
              restaurantId={restaurantId}
              buffet={{ id: capacityFor.buffet_id, name: capacityFor.name }}
              date={date}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
